import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

const LookupVehicleSchema = z.object({
  plate: z.string().optional(),
  plateState: z.string().optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  color: z.string().optional(),
});

export const LookupPermitSchema = z
  .object({
    permit_id: z.string(),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    company: z.string().optional(),
    location_id: z.string(),
    vehicle: LookupVehicleSchema.optional(),
    permit_type: z.string().optional(),
    status: z.string().optional(),
    created_at: zodTimeStamp.optional(),
    updated_at: zodTimeStamp.optional(),
    expires_at: zodTimeStamp.nullable().optional(),
  })
  .passthrough();

export const LookupChalkSchema = z
  .object({
    chalkId: z.string(),
    blockFaceID: z.string().optional(),
    locationId: z.string(),
    vehicle: LookupVehicleSchema.optional(),
    status: z.string().optional(),
    reason: z
      .object({
        label: z.string().optional(),
      })
      .passthrough()
      .optional(),
    createdAt: zodTimeStamp.optional(),
    expiresAt: zodTimeStamp.nullable().optional(),
  })
  .passthrough();

export const LookupReservationSchema = z
  .object({
    reservationId: z.string(),
    locationId: z.string(),
    plate: z.string().optional(),
    plateState: z.string().optional(),
    status: z.string().optional(),
    endTime: zodTimeStamp.optional(),
    createdAt: zodTimeStamp.optional(),
    modifiedAt: zodTimeStamp.optional(),
  })
  .passthrough();

export const LookupValidationSchema = z
  .object({
    validationId: z.string(),
    locationId: z.string(),
    subLocationId: z.string().optional(),
    sublocationDisplayName: z.string().optional(),
    name: z.string().optional(),
    phone: z.string().optional(),
    plate: z.string().optional(),
    plateState: z.string().optional(),
    expiration: zodTimeStamp.optional(),
    createdAt: zodTimeStamp.optional(),
    renewed: z.boolean().optional(),
    status: z.string().optional(),
  })
  .passthrough();

export const LookupViolationSchema = z
  .object({
    violationId: z.string(),
    locationId: z.string(),
    plate: z.string(),
    plateState: z.string().optional(),
    status: z.string().optional(),
    vehicleMake: z.string().optional(),
    vehicleModel: z.string().optional(),
    vehicleColor: z.string().optional(),
    createdAt: zodTimeStamp.optional(),
  })
  .passthrough();

export const LookupExpiredSessionSchema = z
  .object({
    id: z.string(),
    start_time: zodTimeStamp.optional(),
    end_time: zodTimeStamp.optional(),
    type: z.string().optional(),
    plate: z.string().optional(),
    status: z.string().optional(),
  })
  .passthrough();

export const ActiveSessionCheckResponseSchema = z.object({
  valid: z.boolean(),
  permits: z.array(LookupPermitSchema).nullable().optional(),
  chalks: z.array(LookupChalkSchema).nullable().optional(),
  reservation: LookupReservationSchema.nullable().optional(),
  validation: LookupValidationSchema.nullable().optional(),
  violations: z.array(LookupViolationSchema).nullable().optional(),
  expired_session: LookupExpiredSessionSchema.nullable().optional(),
  is_repeat_offender: z.boolean().optional(),
  session_end_time: zodTimeStamp.nullable().optional(),
});

export type ActiveSessionCheckResponse = z.infer<
  typeof ActiveSessionCheckResponseSchema
>;
export type LookupPermit = z.infer<typeof LookupPermitSchema>;
export type LookupChalk = z.infer<typeof LookupChalkSchema>;
export type LookupReservation = z.infer<typeof LookupReservationSchema>;
export type LookupValidation = z.infer<typeof LookupValidationSchema>;
export type LookupViolation = z.infer<typeof LookupViolationSchema>;
export type LookupExpiredSession = z.infer<typeof LookupExpiredSessionSchema>;
